import React from "react";
import Hero from "@/components/Hero";
import SectionHeading from "@/components/SectionHeading";
import ScenarioCard from "@/components/ScenarioCard";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MousePointerClick,
  ListChecks,
  PackageOpen,
  Headphones,
  Wrench,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";

const stepIcons = [MousePointerClick, ListChecks, PackageOpen, Headphones];

const galleryImages = [
  {
    src: "https://images.unsplash.com/photo-1558002038-1055907df827?w=900&q=80",
    cap_tr: "Salon — Hareketle aydınlatma",
    cap_en: "Living room — motion lights",
  },
  {
    src: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=900&q=80",
    cap_tr: "Bahçe — Otomatik damla sulama",
    cap_en: "Garden — drip irrigation",
  },
  {
    src: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80",
    cap_tr: "Çalışma odası — DIY sensör montajı",
    cap_en: "Workshop — DIY sensor build",
  },
  {
    src: "https://images.unsplash.com/photo-1585503418537-88331351ad99?w=900&q=80",
    cap_tr: "Giriş kapısı — Kamera + sensör",
    cap_en: "Entry door — camera + sensor",
  },
  {
    src: "https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=900&q=80",
    cap_tr: "Yatak odası — Günaydın senaryosu",
    cap_en: "Bedroom — morning routine",
  },
  {
    src: "https://images.unsplash.com/photo-1572177812156-58036aae439c?w=900&q=80",
    cap_tr: "Garaj — Kablosuz buton",
    cap_en: "Garage — wireless button",
  },
];

export default function HomePage() {
  const { scenarios, t, lang, loading } = useApp();

  const featured = scenarios.filter((s) => s.featured);
  const others = scenarios.filter((s) => !s.featured);

  return (
    <>
      <Hero />

      {/* Process strip — replaces brand marquee */}
      <section className="border-y border-white/5 bg-[#08090E]">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {t.process.items.map((it, i) => {
            const Icon = stepIcons[i] || Wrench;
            return (
              <div key={it.t} className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <div className="h-10 w-10 rounded-xl bg-amber-500/12 border border-amber-500/30 text-amber-300 flex items-center justify-center">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <span className="absolute -top-2 -right-2 font-mono-tight text-[10px] text-amber-300 bg-black border border-amber-500/40 rounded-full h-5 w-5 flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-medium text-white text-sm leading-tight">
                    {it.t}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug line-clamp-2">
                    {it.d}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Featured bento */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20 sm:py-28">
        <SectionHeading
          label={t.featured.label}
          title={t.featured.title}
          subtitle={t.featured.subtitle}
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 auto-rows-[minmax(280px,_auto)]">
          {!loading && featured.length > 0 && (
            <>
              <div className={`${featured[0]?.bento_class || "md:col-span-7 md:row-span-2"}`}>
                <ScenarioCard scenario={featured[0]} big />
              </div>
              <div className={featured[1]?.bento_class || "md:col-span-5"}>
                <ScenarioCard scenario={featured[1]} />
              </div>
              <div className={featured[2]?.bento_class || "md:col-span-5"}>
                <ScenarioCard scenario={featured[2]} />
              </div>
            </>
          )}
        </div>
      </section>

      {/* All scenarios */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-16">
        <SectionHeading
          label={t.sceneList.label}
          title={t.sceneList.title}
          subtitle={t.sceneList.subtitle}
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {others.map((sc) => (
            <ScenarioCard key={sc.slug} scenario={sc} />
          ))}
        </div>
      </section>

      {/* Process detailed */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-24">
        <SectionHeading label={t.process.label} title={t.process.title} />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.process.items.map((it, i) => {
            const Icon = stepIcons[i] || Wrench;
            return (
              <motion.div
                key={it.t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative rounded-2xl border border-white/[0.07] bg-[#0B0F19] p-7 hover:border-amber-500/30 hover:-translate-y-0.5 transition-all overflow-hidden"
              >
                <div className="absolute top-5 right-5 font-mono-tight text-5xl text-white/[0.04] font-medium">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-300">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="mt-6 font-heading font-medium text-xl tracking-tight">
                  {it.t}
                </h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{it.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Gallery — real projects */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20">
        <SectionHeading
          label={t.inspiration.label}
          title={t.inspiration.title}
          subtitle={t.inspiration.subtitle}
        />
        <div className="mt-12 grid grid-cols-12 gap-3 sm:gap-4">
          {galleryImages.map((img, i) => {
            // Bento layout for gallery
            const spans = [
              "col-span-12 sm:col-span-7 row-span-2 aspect-[16/10]",
              "col-span-6 sm:col-span-5 aspect-[4/3]",
              "col-span-6 sm:col-span-5 aspect-[4/3]",
              "col-span-12 sm:col-span-4 aspect-[4/3]",
              "col-span-6 sm:col-span-4 aspect-[4/3]",
              "col-span-6 sm:col-span-4 aspect-[4/3]",
            ];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`group relative overflow-hidden rounded-2xl border border-white/[0.06] ${spans[i]}`}
              >
                <img
                  src={img.src}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                  <div className="font-heading text-white text-sm sm:text-base font-medium leading-tight">
                    {lang === "tr" ? img.cap_tr : img.cap_en}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA Strip */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 py-20">
        <div className="relative overflow-hidden rounded-3xl border border-amber-500/25 bg-gradient-to-br from-[#1a1306] via-[#0B0F19] to-[#0B0F19] p-10 sm:p-16">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="absolute -bottom-10 -left-10 h-56 w-56 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative max-w-2xl">
            <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-amber-300 mb-4">
              {t.cta.label}
            </div>
            <h2 className="font-heading font-medium text-3xl sm:text-5xl tracking-tight leading-[1.05]">
              {t.cta.title}
            </h2>
            <p className="mt-5 text-gray-300 leading-relaxed">{t.cta.subtitle}</p>
            <Link
              to="/builder"
              className="mt-8 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.45)]"
            >
              {t.cta.btn}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
